import { COMMISSION_PERCENT_DEFAULTS } from "services/config.service";

export const NO_COMMISSION_CODE = "none";
export const NO_COMMISSION_KEY = "none";

export const NO_COMMISSION_OPTION = {
  code: NO_COMMISSION_CODE,
  label: "No Commission",
};

export const RULE_SOURCE_TYPE = {
  fc_motivate: "monthly_profit_share",
  sale_team: "Sale_Team",
  email_manager: "ghl_attribution",
  ace_performance: "stripe_sale",
};

/** Build assignment options from GET /config percentages */
export const buildCommissionOptions = (percentages = COMMISSION_PERCENT_DEFAULTS) => {
  const fc = Number(percentages.fcMotivatePercentage);
  const email = Number(percentages.emailManagerPercentage);
  const ace = Number(percentages.acePerformancePercentage);
  const saleTeam = Number(percentages.saleTeamPercentage);
  const saleTeam5k = Number(percentages.saleTeam5kPercentage);

  return [
    {
      code: "fc_motivate",
      percent: fc,
      label: `FC Motivate (${fc}%)`,
      sourceType: "monthly_profit_share",
    },
    {
      code: "sale_team",
      label: "Sale Team Mentorship",
      sourceType: "Sale_Team",
      percents: [saleTeam, saleTeam5k],
    },
    {
      code: "email_manager",
      percent: email,
      label: `Email Strategist (${email}%)`,
      sourceType: "ghl_attribution",
    },
    {
      code: "ace_performance",
      percent: ace,
      label: `ACE Performance (${ace}%)`,
      sourceType: "stripe_sale",
    },
  ];
};

/** @deprecated Prefer buildCommissionOptions(await getConfig(api)) */
export const COMMISSION_OPTIONS = buildCommissionOptions();

/** @deprecated Prefer buildCommissionOptions(config).find(o => o.code === 'sale_team').percents */
export const SALE_TEAM_PERCENTS = [
  COMMISSION_PERCENT_DEFAULTS.saleTeamPercentage,
  COMMISSION_PERCENT_DEFAULTS.saleTeam5kPercentage,
];

export const isNoCommission = (code, percent) => {
  if (code === NO_COMMISSION_CODE) return true;
  const clearedCode = code == null || code === "";
  const clearedPercent =
    percent == null || percent === "" || Number.isNaN(Number(percent));
  return clearedCode && clearedPercent;
};

export const optionKey = (code, percent) => {
  if (code === NO_COMMISSION_CODE) return NO_COMMISSION_KEY;
  return `${code ?? ""}:${Number(percent)}`;
};

export const findCommissionOption = (code, percent, options = COMMISSION_OPTIONS) => {
  if (isNoCommission(code, percent)) return NO_COMMISSION_OPTION;
  const opt = options.find((item) => item.code === code);
  if (!opt) return null;
  if (opt.percents) {
    const match = opt.percents.find((p) => Number(p) === Number(percent));
    if (match == null) {
      // Historical assignment with a percent no longer in AppConfig
      if (Number.isFinite(Number(percent))) {
        return { ...opt, percent: Number(percent), historical: true };
      }
      return null;
    }
    return { ...opt, percent: Number(match) };
  }
  if (Number(opt.percent) !== Number(percent)) {
    if (Number.isFinite(Number(percent))) {
      return {
        ...opt,
        percent: Number(percent),
        label: opt.label.replace(/\([\d.]+%\)/, `(${Number(percent)}%)`),
        historical: true,
      };
    }
    return null;
  }
  return opt;
};

export const getSourceTypeForRule = (code) =>
  RULE_SOURCE_TYPE[code] ?? null;

export const buildCascaderOptions = (options = COMMISSION_OPTIONS) => [
  { value: NO_COMMISSION_KEY, label: NO_COMMISSION_OPTION.label },
  ...options.map((opt) => {
    if (opt.percents) {
      return {
        value: opt.code,
        label: opt.label,
        children: opt.percents.map((p) => ({
          value: String(p),
          label: `${p}%`,
        })),
      };
    }
    return {
      value: optionKey(opt.code, opt.percent),
      label: opt.label,
    };
  }),
];

export const resolveSelection = (path, options = COMMISSION_OPTIONS) => {
  if (!path?.length) return null;
  if (path[0] === NO_COMMISSION_KEY || path[0] === NO_COMMISSION_CODE) {
    return {
      code: NO_COMMISSION_CODE,
      label: NO_COMMISSION_OPTION.label,
      sourceType: null,
    };
  }
  if (path.length === 2) {
    const code = path[0];
    const percent = Number(path[1]);
    const opt = options.find((item) => item.code === code);
    if (!opt) return null;
    return {
      code,
      percent,
      label: opt.label,
      sourceType: opt.sourceType ?? getSourceTypeForRule(code),
    };
  }
  const [key] = path;
  const [code, percentStr] = String(key).split(":");
  const percent = Number(percentStr);
  const opt = findCommissionOption(code, percent, options);
  if (!opt) return null;
  if (opt.code === NO_COMMISSION_CODE) {
    return {
      code: NO_COMMISSION_CODE,
      label: NO_COMMISSION_OPTION.label,
      sourceType: null,
    };
  }
  return {
    code: opt.code,
    percent: opt.percent,
    label: opt.label,
    sourceType: opt.sourceType ?? getSourceTypeForRule(opt.code),
  };
};

export const selectionToPath = (code, percent, options = COMMISSION_OPTIONS) => {
  if (isNoCommission(code, percent)) return [NO_COMMISSION_KEY];
  const opt = findCommissionOption(code, percent, options);
  if (!opt) return [];
  if (opt.percents) return [opt.code, String(opt.percent)];
  return [optionKey(opt.code, opt.percent)];
};
